import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import adminApi, { clearToken } from "../lib/adminApi";
import { coverSrc } from "../lib/api";
import { Sparkles, Loader2, Trash2, RefreshCw, Eye, LogOut, CheckCircle2, EyeOff, Save, Image as ImageIcon, Search, BarChart3, X } from "lucide-react";
import { toast } from "sonner";

const STATUS = { published: { label: "Publié", cls: "bg-emerald-100 text-emerald-700" }, review: { label: "À valider", cls: "bg-amber-100 text-amber-700" }, draft: { label: "Brouillon", cls: "bg-zinc-100 text-zinc-600" } };

export default function Admin() {
  const nav = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [gsc, setGsc] = useState(null);
  const [gscData, setGscData] = useState(null);
  const [gscJson, setGscJson] = useState("");
  const [gscBusy, setGscBusy] = useState(false);
  const [showGscForm, setShowGscForm] = useState(false);

  const loadGsc = async () => {
    try {
      const { data } = await adminApi.get("/admin/gsc/status");
      setGsc(data);
      if (data.configured) {
        try { const r = await adminApi.get("/admin/gsc/analytics"); setGscData(r.data); }
        catch (e) { toast.error(e.response?.data?.detail || "Erreur Search Console"); }
      }
    } catch (e) { /* ignore */ }
  };

  const saveGsc = async () => {
    setGscBusy(true);
    try {
      await adminApi.put("/admin/gsc/config", { service_account_json: gscJson });
      toast.success("Search Console connecté");
      setGscJson(""); setShowGscForm(false);
      await loadGsc();
    } catch (e) { toast.error(e.response?.data?.detail || "JSON invalide"); }
    finally { setGscBusy(false); }
  };

  const disconnectGsc = async () => {
    if (!window.confirm("Déconnecter Google Search Console ?")) return;
    await adminApi.delete("/admin/gsc/config");
    setGsc({ configured: false }); setGscData(null);
    toast.success("Search Console déconnecté");
  };

  const load = async () => {
    try {
      const { data } = await adminApi.get("/admin/articles");
      setArticles(data);
    } catch (e) {
      if (e.response?.status === 401) { clearToken(); nav("/admin/login"); }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    adminApi.get("/auth/me").then(() => { load(); loadGsc(); }).catch(() => { clearToken(); nav("/admin/login"); });
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await adminApi.post("/admin/articles/generate");
      toast.success(`Actualité générée : ${data.title}`);
      await load();
      toast.message("Génération de l'image de couverture…");
      try { await adminApi.post(`/admin/articles/${data.id}/cover`); await load(); }
      catch { toast.error("Image non générée (article créé sans visuel)"); }
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de la génération"); }
    finally { setGenerating(false); }
  };

  const action = async (id, fn, msg) => {
    setBusyId(id);
    try { await fn(); toast.success(msg); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusyId(null); }
  };

  const saveEdit = async () => {
    const id = editing.id;
    setBusyId(id);
    try {
      await adminApi.put(`/admin/articles/${id}`, {
        title: editing.title, meta_title: editing.meta_title,
        meta_description: editing.meta_description, excerpt: editing.excerpt, cta: editing.cta,
      });
      toast.success("Modifications enregistrées");
      setEditing(null); await load();
    } catch (e) { toast.error("Erreur d'enregistrement"); }
    finally { setBusyId(null); }
  };

  const counts = { total: articles.length, review: articles.filter(a => a.status === "review").length, published: articles.filter(a => a.status === "published").length };

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-semibold text-zinc-900">Actualités — Serrurerie 77</h1>
            <p className="text-xs text-zinc-500">{counts.total} articles · {counts.published} publiés · {counts.review} à valider</p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="generate-article-btn" onClick={generate} disabled={generating}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {generating ? "Génération…" : "Générer une actualité"}
            </button>
            <button data-testid="logout-btn" onClick={() => { clearToken(); nav("/admin/login"); }}
              className="inline-flex items-center gap-2 rounded-lg border border-zinc-200 px-3 py-2.5 text-sm text-zinc-600 hover:bg-zinc-50"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Google Search Console */}
        <section className="mb-8 bg-white rounded-xl border border-zinc-200 p-5" data-testid="gsc-panel">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-indigo-600" />
              <h2 className="font-semibold text-zinc-900">Google Search Console</h2>
            </div>
            {gsc?.configured && (
              <div className="flex items-center gap-2">
                <button onClick={loadGsc} className="p-2 rounded-lg border border-zinc-200 hover:bg-zinc-50" title="Rafraîchir"><RefreshCw className="h-4 w-4 text-zinc-500" /></button>
                <button onClick={disconnectGsc} className="text-xs text-red-500 hover:underline">Déconnecter</button>
              </div>
            )}
          </div>

          {!gsc?.configured ? (
            <div>
              <p className="text-sm text-zinc-500 mb-3">Connecte ta propriété pour suivre impressions, clics et positions, et orienter les sujets IA selon tes vraies données.</p>
              {!showGscForm ? (
                <button data-testid="gsc-connect-btn" onClick={() => setShowGscForm(true)} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"><Search className="h-4 w-4" /> Connecter Search Console</button>
              ) : (
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Colle ici le contenu du fichier JSON de compte de service Google</label>
                  <textarea data-testid="gsc-json-input" value={gscJson} onChange={(e) => setGscJson(e.target.value)} rows={5} placeholder='{ "type": "service_account", "client_email": "...", "private_key": "..." }'
                    className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-xs font-mono mb-3" />
                  <div className="flex gap-2">
                    <button data-testid="gsc-save-btn" onClick={saveGsc} disabled={gscBusy || !gscJson.trim()} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">{gscBusy && <Loader2 className="h-4 w-4 animate-spin" />} Connecter</button>
                    <button onClick={() => { setShowGscForm(false); setGscJson(""); }} className="rounded-lg border border-zinc-200 px-4 py-2 text-sm">Annuler</button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <p className="text-xs text-zinc-400 mb-3">{gsc.property} · {gscData ? `${gscData.range_days} derniers jours` : "chargement…"}</p>
              {gscData && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    {[["Impressions", gscData.totals.impressions], ["Clics", gscData.totals.clicks], ["CTR", `${gscData.totals.ctr}%`], ["Position moy.", gscData.totals.position]].map(([l, v]) => (
                      <div key={l} className="rounded-lg bg-zinc-50 border border-zinc-200 p-3"><div className="text-xs text-zinc-500">{l}</div><div className="text-lg font-semibold text-zinc-900">{v}</div></div>
                    ))}
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium text-zinc-600 mb-2 flex items-center gap-1"><BarChart3 className="h-3.5 w-3.5" /> Top requêtes</p>
                      <div className="space-y-1">
                        {gscData.top_queries.slice(0, 8).map((q, i) => (
                          <div key={i} className="flex items-center justify-between text-xs border-b border-zinc-100 py-1"><span className="truncate text-zinc-700">{q.key}</span><span className="text-zinc-400 shrink-0 ml-2">{q.impressions} imp · pos {q.position}</span></div>
                        ))}
                        {gscData.top_queries.length === 0 && <p className="text-xs text-zinc-400">Aucune donnée pour l'instant.</p>}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-zinc-600 mb-2 flex items-center gap-1"><BarChart3 className="h-3.5 w-3.5" /> Top pages</p>
                      <div className="space-y-1">
                        {gscData.top_pages.slice(0, 8).map((p, i) => (
                          <div key={i} className="flex items-center justify-between text-xs border-b border-zinc-100 py-1"><span className="truncate text-zinc-700">{p.key.replace("https://serrurerie-77.com", "")}</span><span className="text-zinc-400 shrink-0 ml-2">{p.clicks} clics</span></div>
                        ))}
                        {gscData.top_pages.length === 0 && <p className="text-xs text-zinc-400">Aucune donnée pour l'instant.</p>}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </section>

        {loading ? <p className="text-zinc-400">Chargement…</p> : (
          <div className="space-y-3" data-testid="admin-articles-list">
            {articles.map((a) => (
              <div key={a.id} className="bg-white rounded-xl border border-zinc-200 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 min-w-0">
                    <img src={coverSrc(a)} alt="" className="hidden sm:block h-16 w-24 rounded-lg object-cover border border-zinc-200 shrink-0" />
                    <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS[a.status]?.cls || "bg-zinc-100"}`}>{STATUS[a.status]?.label || a.status}</span>
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500">{a.category}</span>
                      {a.source === "ai" && <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">IA</span>}
                      {a.risk_level === "sensitive" && <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-600">Sensible</span>}
                      <span className="text-[11px] text-zinc-400">{a.date}{a.updated_at ? ` · maj ${a.updated_at.slice(0, 10)}` : ""}</span>
                    </div>
                    <h3 className="font-medium text-zinc-900 truncate">{a.title}</h3>
                    <p className="text-sm text-zinc-500 line-clamp-2">{a.excerpt}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button title="Générer / régénérer l'image" disabled={busyId === a.id} onClick={() => action(a.id, () => adminApi.post(`/admin/articles/${a.id}/cover`), "Image générée")} className="p-2 rounded-lg border border-zinc-200 hover:bg-zinc-50" data-testid={`cover-${a.slug}`}>{busyId === a.id ? <Loader2 className="h-4 w-4 animate-spin text-zinc-500" /> : <ImageIcon className="h-4 w-4 text-zinc-500" />}</button>
                    {a.status === "published" && <a href={`/conseils/${a.slug}`} target="_blank" rel="noreferrer" title="Aperçu" className="p-2 rounded-lg border border-zinc-200 hover:bg-zinc-50"><Eye className="h-4 w-4 text-zinc-500" /></a>}
                    <button title="Éditer" onClick={() => setEditing(a)} className="p-2 rounded-lg border border-zinc-200 hover:bg-zinc-50" data-testid={`edit-${a.slug}`}><Save className="h-4 w-4 text-zinc-500" /></button>
                    <button title="Rafraîchir (IA)" disabled={busyId === a.id} onClick={() => action(a.id, () => adminApi.post(`/admin/articles/${a.id}/refresh`), "Article mis à jour par l'IA")} className="p-2 rounded-lg border border-zinc-200 hover:bg-zinc-50">{busyId === a.id ? <Loader2 className="h-4 w-4 animate-spin text-zinc-500" /> : <RefreshCw className="h-4 w-4 text-zinc-500" />}</button>
                    {a.status !== "published"
                      ? <button title="Publier" disabled={busyId === a.id} onClick={() => action(a.id, () => adminApi.post(`/admin/articles/${a.id}/publish`), "Article publié")} className="p-2 rounded-lg bg-emerald-600 hover:bg-emerald-700" data-testid={`publish-${a.slug}`}><CheckCircle2 className="h-4 w-4 text-white" /></button>
                      : <button title="Dépublier" disabled={busyId === a.id} onClick={() => action(a.id, () => adminApi.post(`/admin/articles/${a.id}/unpublish`), "Article dépublié")} className="p-2 rounded-lg border border-zinc-200 hover:bg-zinc-50"><EyeOff className="h-4 w-4 text-zinc-500" /></button>}
                    <button title="Supprimer" disabled={busyId === a.id} onClick={() => { if (window.confirm("Supprimer cet article ?")) action(a.id, () => adminApi.delete(`/admin/articles/${a.id}`), "Article supprimé"); }} className="p-2 rounded-lg border border-zinc-200 hover:bg-red-50"><Trash2 className="h-4 w-4 text-red-500" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="edit-modal">
            <h3 className="font-semibold text-zinc-900 mb-4">Éditer l'actualité</h3>
            {[["title", "Titre (H1)"], ["meta_title", "Meta title"], ["excerpt", "Extrait"], ["cta", "Appel à l'action"]].map(([k, label]) => (
              <div key={k} className="mb-3">
                <label className="block text-xs text-zinc-500 mb-1">{label}</label>
                <input value={editing[k] || ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm" />
              </div>
            ))}
            <div className="mb-4">
              <label className="block text-xs text-zinc-500 mb-1">Meta description</label>
              <textarea value={editing.meta_description || ""} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} rows={2} className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm" />
            </div>
            <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-3 mb-4 max-h-48 overflow-y-auto">
              <p className="text-xs text-zinc-400 mb-2">Aperçu du contenu ({editing.sections?.length || 0} sections)</p>
              {editing.sections?.map((s, i) => <p key={i} className="text-sm text-zinc-700 font-medium">• {s.h2}</p>)}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="rounded-lg border border-zinc-200 px-4 py-2 text-sm">Annuler</button>
              <button onClick={saveEdit} disabled={busyId === editing.id} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700" data-testid="save-edit-btn">
                {busyId === editing.id && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
